import { cn } from '@amore/ui/cn'
import { Badge } from '@amore/ui/components/badge'
import { ArrowRight, BedDouble, Car } from 'lucide-react'
import { dietLabels } from '@/lib/labels'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Party, Person, ReplyAnswer, WebReply } from '@/mock/types'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { type AttendanceState, attendanceStyles, stateOf } from './attendance'
import { partyTitle, personName } from './model'
import { received } from './replies'

/**
 * Reviewing one web reply: what it changes in the guest list, person by person,
 * before anything is written. Replies only come from invitations on the list.
 * "Zapsat do seznamu" applies it (with undo); "Zamítnout" sets it aside.
 */
export function ReviewSheet({ reply, onClose }: { reply: WebReply; onClose: () => void }) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const party = data.parties.find((item) => item.id === reply.partyId)
  const pending = reply.status === 'new'

  const apply = () => {
    if (!party) return
    const snapshot = data
    const result = applyReply(reply, party)
    update((current) => ({
      ...current,
      parties: replaceById(current.parties, result.id, result),
      webReplies: replaceById(current.webReplies, reply.id, { status: 'applied' }),
    }))
    showToast({
      title: 'Zapsáno do seznamu',
      description: partyTitle(result),
      tone: 'success',
      action: {
        label: 'Vrátit zpět',
        altText: 'Vrátit odpověď ke kontrole',
        onClick: () =>
          update((current) => ({
            ...current,
            parties: snapshot.parties,
            webReplies: snapshot.webReplies,
          })),
      },
    })
    onClose()
  }
  const setStatus = (status: WebReply['status']) => {
    update((current) => ({
      ...current,
      webReplies: replaceById(current.webReplies, reply.id, { status }),
    }))
    showToast(
      status === 'dismissed'
        ? {
            title: 'Odpověď zamítnuta',
            description: 'Do seznamu se nic nezapsalo.',
            action: {
              label: 'Vrátit zpět',
              altText: 'Vrátit odpověď ke kontrole',
              onClick: () =>
                update((current) => ({
                  ...current,
                  webReplies: replaceById(current.webReplies, reply.id, { status: 'new' }),
                })),
            },
          }
        : { title: 'Odpověď je zpátky ke kontrole' },
    )
    onClose()
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      eyebrow="Odpověď z webu"
      title={reply.name}
      description={
        <span className="flex flex-wrap items-center gap-2">
          {received(reply.receivedAt)}
          {pending ? null : (
            <Badge tone={reply.status === 'applied' ? 'success' : 'neutral'}>
              {reply.status === 'applied' ? 'Zapsáno' : 'Zamítnuto'}
            </Badge>
          )}
        </span>
      }
      primaryAction={
        canEdit && pending ? { label: 'Zapsat do seznamu', onClick: apply } : undefined
      }
      secondaryAction={
        canEdit
          ? pending
            ? { label: 'Zamítnout', onClick: () => setStatus('dismissed') }
            : reply.status === 'dismissed'
              ? { label: 'Vrátit ke kontrole', onClick: () => setStatus('new') }
              : undefined
          : undefined
      }
    >
      <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
        {reply.message ? <Message text={reply.message} /> : null}

        <section className="space-y-4">
          <h3 className="text-sm font-semibold">
            {pending ? 'Co se v seznamu změní' : 'Co poslali'}
          </h3>
          <ul className="space-y-4">
            {reply.answers.map((answer) => (
              <AnswerRow
                key={answer.personId}
                answer={answer}
                person={party?.persons.find((item) => item.id === answer.personId)}
              />
            ))}
          </ul>
          <NeedsChange party={party} reply={reply} />
        </section>
      </div>
    </DetailSheet>
  )
}

/** One person: their state before → after, and what else changes (name, diet). */
function AnswerRow({ answer, person }: { answer: ReplyAnswer; person?: Person }) {
  const before: AttendanceState | null = person ? stateOf(person) : null
  const after: AttendanceState = answer.attendance
  const name = `${answer.firstName} ${answer.lastName}`.trim()
  const changes: string[] = []
  if (!person) changes.push(answer.child ? 'přidali dítě' : 'přidali doprovod')
  if (person?.plusOne && !person.firstName && name) changes.push('doplnil jméno doprovodu')
  const addedDiets = answer.diets.filter((diet) => !person?.diets.includes(diet))
  const removedDiets = (person?.diets ?? []).filter((diet) => !answer.diets.includes(diet))
  for (const diet of addedDiets) changes.push(dietLabels[diet].toLowerCase())
  for (const diet of removedDiets) changes.push(`už ne ${dietLabels[diet].toLowerCase()}`)
  const same = before === after && changes.length === 0
  return (
    <li className="flex items-start gap-3">
      <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
        {before && before !== after ? (
          <>
            <StateIcon state={before} faded />
            <ArrowRight aria-hidden="true" className="size-3.5 text-muted-foreground" />
          </>
        ) : null}
        <StateIcon state={after} />
      </span>
      <span className="min-w-0 space-y-0.5">
        <span className="block text-sm font-medium">
          {name || (person ? personName(person) : 'Bez jména')}
        </span>
        <span className="block text-sm text-muted-foreground">
          {same
            ? 'beze změny'
            : [before !== after ? attendanceStyles[after].label.toLowerCase() : null, ...changes]
                .filter(Boolean)
                .join(' · ')}
        </span>
      </span>
    </li>
  )
}

function StateIcon({ state, faded = false }: { state: AttendanceState; faded?: boolean }) {
  const { icon: Icon, label, className } = attendanceStyles[state]
  return (
    <span
      className={cn(
        'grid size-6 place-items-center rounded-full',
        className,
        faded && 'opacity-50',
      )}
    >
      <Icon aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
      <span className="sr-only">{label}</span>
    </span>
  )
}

/** Accommodation and transport, shown only when the reply asks for them or drops them. */
function NeedsChange({ party, reply }: { party?: Party; reply: WebReply }) {
  const rows = [
    {
      icon: BedDouble,
      label: 'Ubytování',
      before: party?.accommodation ?? false,
      after: reply.accommodation,
    },
    { icon: Car, label: 'Odvoz', before: party?.transport ?? false, after: reply.transport },
  ].filter((row) => row.after || row.before)
  if (rows.length === 0) return null
  return (
    <ul className="space-y-2 border-t border-border/70 pt-4">
      {rows.map(({ icon: Icon, label, before, after }) => (
        <li key={label} className="flex items-center gap-3 text-sm">
          <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
          <span className="font-medium">{label}</span>
          <span className="text-muted-foreground">
            {before === after
              ? after
                ? 'potřebují, beze změny'
                : ''
              : after
                ? 'nově potřebují'
                : 'už nepotřebují'}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** The guest's note, as a quiet section with its own heading. */
export function Message({ text }: { text: string }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Vzkaz</h3>
      <p className="text-[15px] leading-relaxed text-foreground/85">„{text}“</p>
    </section>
  )
}

/** Writes the reply into the list: updates the invitation's people or creates the invitation. */
function applyReply(reply: WebReply, party: Party): Party {
  const persons = party.persons.map((person) => {
    const answer = reply.answers.find((item) => item.personId === person.id)
    return answer
      ? {
          ...person,
          firstName: answer.firstName || person.firstName,
          lastName: answer.lastName || person.lastName,
          attendance: answer.attendance,
          diets: answer.diets,
        }
      : person
  })
  // A companion or child the guest added themselves (allowed in Nastavení) joins the invitation.
  const added = reply.answers
    .filter((answer) => !party.persons.some((person) => person.id === answer.personId))
    .map((answer) => ({
      id: answer.personId,
      firstName: answer.firstName,
      lastName: answer.lastName,
      attendance: answer.attendance,
      child: Boolean(answer.child),
      diets: answer.diets,
    }))
  return {
    ...party,
    persons: [...persons, ...added],
    replied: true,
    repliedAt: reply.receivedAt.slice(0, 10),
    message: reply.message || party.message,
    accommodation: reply.accommodation,
    transport: reply.transport,
  }
}
